import { Modal } from "newui/components/ui/Modal";

export type UserStatusAction = "deactivate" | "reactivate";

interface UserStatusConfirmModalProps {
  action: UserStatusAction;
  firstName?: string;
  isLoading?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Shared confirmation dialog for deactivating / reactivating a user. Used by the
 * user list (table row actions + page) and the UserDrawer so the wording and
 * behaviour stay in one place.
 *
 * Deactivation wipes credentials; reactivation sends a fresh re-invite — the
 * copy spells this out.
 */
export const UserStatusConfirmModal = ({
  isOpen,
  action,
  firstName,
  isLoading = false,
  onConfirm,
  onClose,
}: UserStatusConfirmModalProps) => {
  const isDeactivate = action === "deactivate";
  const who = firstName ?? "this user";

  return (
    <Modal
      isOpen={isOpen}
      modalType="alert"
      iconType="warning"
      title={isDeactivate ? `Deactivate ${who}?` : `Reactivate ${who}?`}
      primaryButton={{
        text: isDeactivate ? "Deactivate" : "Reactivate",
        btntype: isDeactivate ? "danger" : "primary",
        isLoading,
        disabled: isLoading,
        onClick: onConfirm,
      }}
      secondaryButton={{
        text: "Cancel",
        btntype: "secondary",
        onClick: onClose,
      }}
      onClose={onClose}
    >
      {isDeactivate ? (
        <div className="text-left text-md space-y-3 text-gray-600">
          <p>This will deactivate the user, sign them out and clear their:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>password</li>
            <li>email verification</li>
            <li>any two-factor authentication setup</li>
          </ul>
          <p>
            You can restore access later, which will send a fresh invitation for them to
            re-register.
          </p>
          <p>All existing roles are preserved.</p>
        </div>
      ) : (
        <div className="text-left text-md space-y-3 text-gray-600">
          <p>
            This will send a fresh invitation to the user&apos;s email address so they can
            re-register, setting a new password (and two-factor authentication if applicable).
          </p>
          <p>Their existing roles are preserved.</p>
        </div>
      )}
    </Modal>
  );
};
